<template>
  <div class="min-h-full p-4">
    <div class="container">
      <button
        type="button"
        @click="handleBack"
        class="mb-6 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white px-3 py-1.5 rounded-xl border border-indigo-400 border-opacity-20 font-medium transition-all duration-300 flex items-center gap-2 hover:from-indigo-600 hover:to-indigo-700"
      >
        <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
        返回
      </button>

      <div class="header">
        <div class="inline-flex items-center justify-center w-16 h-16 gradient-button rounded-full mb-4 shadow-lg">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>
        </div>
        <h1 class="title">API Key 管理</h1>
        <p class="subtitle">创建 API Key 后，可用于调用公共 HTTP 接口与 MCP 服务</p>
      </div>

      <div v-if="!authUser" class="main-card text-center py-10">
        <p class="text-gray-600 mb-4">请先登录后再管理 API Key</p>
        <button
          type="button"
          class="gradient-button text-white px-6 py-3 rounded-xl font-medium"
          @click="goLogin"
        >
          去登录
        </button>
      </div>

      <template v-else>
        <div class="main-card">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h2 class="section-title mb-1">我的 API Key</h2>
              <p class="section-desc">每个账号最多 5 个 Key；明文仅在创建时显示一次，请妥善保存</p>
            </div>
            <button
              type="button"
              class="gradient-button text-white px-5 py-2.5 rounded-xl font-medium shrink-0 disabled:opacity-50"
              :disabled="creating || keys.length >= 5"
              @click="openCreateDialog"
            >
              创建 API Key
            </button>
          </div>

          <div v-if="loading" class="text-center py-8 text-gray-500">加载中…</div>
          <div v-else-if="keys.length === 0" class="text-center py-8 text-gray-500">暂无 API Key，点击上方按钮创建</div>
          <div v-else class="space-y-3">
            <div v-for="item in keys" :key="item.id" class="key-row">
              <div class="min-w-0 flex-1">
                <div class="font-semibold text-gray-900">{{ item.name }}</div>
                <div class="text-sm text-gray-500 font-mono mt-1">{{ item.key_prefix }}</div>
                <div class="text-xs text-gray-400 mt-1">
                  创建于 {{ formatTime(item.created_at) }}
                  <span v-if="item.last_used_at"> · 最近使用 {{ formatTime(item.last_used_at) }}</span>
                </div>
              </div>
              <button
                type="button"
                class="shrink-0 text-sm text-red-600 hover:text-red-700 px-3 py-1.5 rounded-lg hover:bg-red-50"
                :disabled="revokingId === item.id"
                @click="revokeKey(item)"
              >
                {{ revokingId === item.id ? '撤销中…' : '撤销' }}
              </button>
            </div>
          </div>
        </div>

        <div class="main-card">
          <h2 class="section-title">使用说明</h2>
          <div class="notes">
            <div class="note-item"><span class="note-dot"></span>HTTP 接口：<router-link to="/docs/api" class="text-blue-600 hover:underline">查看 HTTP 接口文档</router-link></div>
            <div class="note-item"><span class="note-dot"></span>MCP 对接：<router-link to="/docs/mcp" class="text-blue-600 hover:underline">查看 MCP 对接文档</router-link></div>
            <div class="note-item"><span class="note-dot"></span>公共接口地址：<code class="code-inline">{{ publicApiUrl }}</code>（HTTP <code class="code-inline">/v1/query</code>、MCP <code class="code-inline">/mcp</code>）</div>
            <div class="note-item"><span class="note-dot"></span>鉴权方式：请求头 <code class="code-inline">X-API-Key: ph_...</code></div>
          </div>
        </div>
      </template>
    </div>

    <!-- 创建对话框 -->
    <div v-if="showCreateDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div class="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">创建 API Key</h3>
        <label class="block text-sm font-medium text-gray-700 mb-2">名称（便于识别用途）</label>
        <input
          v-model="newKeyName"
          type="text"
          maxlength="64"
          placeholder="例如：生产环境脚本"
          class="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none mb-4"
          @keyup.enter="createKey"
        />
        <div class="flex gap-3 justify-end">
          <button type="button" class="px-4 py-2 text-gray-600 hover:text-gray-800" @click="closeCreateDialog">取消</button>
          <button
            type="button"
            class="gradient-button text-white px-5 py-2 rounded-xl font-medium disabled:opacity-50"
            :disabled="creating || !newKeyName.trim()"
            @click="createKey"
          >
            {{ creating ? '创建中…' : '创建' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 明文 Key 展示（仅一次） -->
    <div v-if="createdKey" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4">
      <div class="bg-white rounded-2xl p-6 max-w-lg w-full shadow-xl">
        <h3 class="text-lg font-semibold text-gray-900 mb-2">请保存您的 API Key</h3>
        <p class="text-sm text-amber-800 bg-amber-50 rounded-lg px-3 py-2 mb-4">此 Key 只会显示一次，关闭后将无法再次查看完整内容。</p>
        <div class="bg-gray-50 rounded-xl p-4 font-mono text-sm break-all text-gray-900 mb-4 select-all">{{ createdKey }}</div>
        <div class="flex gap-3 justify-end">
          <button type="button" class="px-4 py-2 text-blue-600 hover:text-blue-700" @click="copyCreatedKey">{{ copied ? '已复制' : '复制' }}</button>
          <button type="button" class="gradient-button text-white px-5 py-2 rounded-xl font-medium" @click="closeCreatedKey">我已保存</button>
        </div>
      </div>
    </div>

    <div v-if="toast" class="fixed top-4 left-1/2 -translate-x-1/2 z-[10020] px-6 py-3 rounded-xl shadow-lg text-white font-semibold" :class="toastError ? 'bg-red-500' : 'bg-blue-600'">
      {{ toast }}
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { getUser, onAuthChanged } from '../utils/auth.js';
import { extractData } from '../utils/api.js';
import { PUBLIC_API_URL } from '../utils/seo.js';

export default {
  name: 'ApiKeys',
  data() {
    return {
      authUser: getUser(),
      publicApiUrl: PUBLIC_API_URL,
      keys: [],
      loading: false,
      creating: false,
      revokingId: null,
      showCreateDialog: false,
      newKeyName: '',
      createdKey: '',
      copied: false,
      toast: '',
      toastError: false
    };
  },
  mounted() {
    this.unsubscribeAuth = onAuthChanged(this.onAuthEvent);
    if (this.authUser) this.loadKeys();
  },
  beforeUnmount() {
    if (this.unsubscribeAuth) this.unsubscribeAuth();
  },
  methods: {
    onAuthEvent() {
      this.authUser = getUser();
      if (this.authUser) this.loadKeys();
      else this.keys = [];
    },
    handleBack() {
      this.$router.push('/single');
    },
    goLogin() {
      this.$router.push({ path: '/single', query: { auth: 'login', redirect: '/settings/api-keys' } });
    },
    showToast(msg, isError = false) {
      this.toast = msg;
      this.toastError = isError;
      setTimeout(() => { this.toast = ''; }, 3000);
    },
    formatTime(s) {
      if (!s) return '';
      try {
        return new Date(s).toLocaleString('zh-CN');
      } catch {
        return s;
      }
    },
    async loadKeys() {
      this.loading = true;
      try {
        const res = await axios.get('/api/api-keys');
        const data = extractData(res);
        this.keys = data?.keys || [];
      } catch (e) {
        this.showToast(e?.response?.data?.message || '加载失败', true);
      } finally {
        this.loading = false;
      }
    },
    openCreateDialog() {
      this.newKeyName = '';
      this.showCreateDialog = true;
    },
    closeCreateDialog() {
      this.showCreateDialog = false;
    },
    async createKey() {
      const name = this.newKeyName.trim();
      if (!name) return;
      this.creating = true;
      try {
        const res = await axios.post('/api/api-keys', { name });
        const data = extractData(res);
        this.createdKey = data.key;
        this.copied = false;
        this.closeCreateDialog();
        await this.loadKeys();
      } catch (e) {
        this.showToast(e?.response?.data?.message || '创建失败', true);
      } finally {
        this.creating = false;
      }
    },
    closeCreatedKey() {
      this.createdKey = '';
    },
    async copyCreatedKey() {
      try {
        await navigator.clipboard.writeText(this.createdKey);
        this.copied = true;
      } catch {
        this.showToast('复制失败，请手动选择复制', true);
      }
    },
    async revokeKey(item) {
      if (!confirm(`确定撤销 API Key「${item.name}」？撤销后无法恢复。`)) return;
      this.revokingId = item.id;
      try {
        await axios.delete(`/api/api-keys/${item.id}`);
        await this.loadKeys();
        this.showToast('已撤销');
      } catch (e) {
        this.showToast(e?.response?.data?.message || '撤销失败', true);
      } finally {
        this.revokingId = null;
      }
    }
  }
};
</script>

<style scoped>
.container {
  max-width: 720px;
  margin: 0 auto;
}
.header {
  text-align: center;
  margin-bottom: 2rem;
}
.title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.5rem;
}
.subtitle {
  color: #6b7280;
}
.main-card {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(12px);
  border-radius: 1.5rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.3);
}
.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
}
.section-desc {
  font-size: 0.875rem;
  color: #6b7280;
}
.key-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 1rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
}
.notes {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.note-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #4b5563;
  line-height: 1.6;
}
.note-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #3b82f6;
  margin-top: 0.5rem;
  flex-shrink: 0;
}
.code-inline {
  background: #f3f4f6;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  font-family: ui-monospace, monospace;
  font-size: 0.8125rem;
}
</style>
